import { service } from '@maia/cart';
import { mapper } from '@maia/icons';
import {
  Button,
  cardStyles,
  FeaturedIcon,
  getErrors,
  Typography,
} from '@maia/ui';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { encodeQueryItem } from 'ufo';
import * as v from 'valibot';
import { getDefaultFormValues } from '../../../../features/add-insurance/object-reference-form';
import { getUpsellproductsOptions } from '../../../../features/add-insurance/upsell-products/queryOptions';
import {
  parseProductsFromQuery,
  replaceProductGuids,
} from '../../../../features/add-insurance/utils';
import { useAppForm } from '../../../../features/form/dynamic-form/dynamic-form';
import { mapToAnswer } from '../../../../features/form/utils/map-to-answers';
import { useMemo } from 'react';
import { queryClient } from '../../../../../src/main';
import { BrokerDto } from '@maia/shared';

export const Route = createFileRoute(
  '/_authenticated/add-insurance/$customer/_layout/ob',
)({
  component: RouteComponent,
  loaderDeps: ({ search: { products } }) => ({ products }),
  loader: async ({
    context: { queryClient },
    params: { customer: guid },
    deps: { products },
  }) => {
    const selectedProducts = parseProductsFromQuery(products);
    const upsellProducts = await queryClient.ensureQueryData(
      getUpsellproductsOptions({ customerGuid: guid }),
    );
    return upsellProducts.filter((p) =>
      selectedProducts.some((selected) => {
        const [guid, riskPool] = selected.split('|');
        if (!p.riskPool) return p.guid === guid;
        return p.guid === guid && p.riskPool === riskPool;
      }),
    );
  },
});

function RouteComponent() {
  const data = Route.useLoaderData();
  const { customer } = Route.useParams();
  const navigate = Route.useNavigate();

  const { mutate: getProducts, isPending: getProductsPending } = useMutation({
    mutationFn: (params: Parameters<typeof service.getProducts>[number]) =>
      service.getProducts(params),
    onSuccess: (response, request) => {
      const newGuids = replaceProductGuids(
        data.map((upsellProducts) => upsellProducts.guid),
        response,
      );
      const query = encodeQueryItem('guid', newGuids);
      return navigate({
        to: '/add-insurance/$customer/cart/$cart/product/$product',
        params: {
          cart: request.cartGuid,
          customer,
          product: newGuids[0],
        },
        search: {
          products: query,
        },
      });
    },
  });

  const { mutate: createCart, isPending: createCartPending } = useMutation({
    mutationFn: (
      params: Parameters<typeof service.createCart>[number]['body'],
    ) => {
      const me = queryClient.getQueryData<BrokerDto>(['getSelf']);
      return service.createCart({
        customerGuid: customer,
        body: {
          ...params,
          salesChannel: me?.salesChannel ?? '',
        },
      });
    },
    onSuccess: ({ cart_guid }) =>
      getProducts({
        cartGuid: cart_guid,
        customerGuid: customer,
      }),
  });

  const obProducts = useMemo(
    () => data.filter((product) => product.objectCode === 'OB'),
    [data],
  );

  const form = useAppForm({
    defaultValues: getDefaultFormValues(obProducts),
    onSubmit: ({ value }) => {
      const answers = mapToAnswer(value);
      const mergedAnswers = data.map((product) => {
        const answer = answers.find(
          (answer) => answer.guid.split('|')[0] === product.guid,
        );
        return {
          guid: product.guid,
          answer: answer?.answer ?? undefined,
          riskpool: product.riskPool,
        };
      });
      createCart({
        selectedProducts: mergedAnswers.map((answer) => ({
          guid: answer.guid,
          objectReference: answer.answer,
          riskPool: answer.riskpool,
        })),
      });
    },
  });

  return (
    <div className="area-page-content content-grid">
      <div className="col-span-full">
        <form
          className="flex flex-col gap-y-32"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.AppForm>
            {obProducts.map((product) => (
              <div
                key={`${product.guid}|${product.riskPool}`}
                className={cardStyles({
                  variant: 'outlined',
                  className: 'flex flex-col gap-y-32 p-24',
                })}
              >
                <div className="flex items-center gap-x-20">
                  <FeaturedIcon
                    icon={mapper(product.icon)}
                    variant="light-square"
                    size="md"
                  />
                  <Typography variant="body-l" weight="medium">
                    {product.name}
                  </Typography>
                </div>
                <div className="flex items-end gap-x-12">
                  <form.AppField
                    name={`${product.guid}|${product.riskPool}`}
                    validators={{
                      onChange: v.pipe(
                        v.string('Kenteken is verplicht'),
                        v.trim(),
                        v.minLength(1, 'Kenteken is verplicht'),
                      ),
                    }}
                  >
                    {(field) => (
                      <field.LicensePlateField
                        label="Kenteken"
                        error={getErrors(field)}
                      />
                    )}
                  </form.AppField>
                </div>
              </div>
            ))}

            <form.Subscribe selector={(state) => state.isValid}>
              {(isValid) => (
                <Button
                  className="w-min"
                  type="submit"
                  intent="primary"
                  size="large"
                  isDisabled={
                    createCartPending || getProductsPending || !isValid
                  }
                  isLoading={createCartPending || getProductsPending}
                >
                  Selecteer
                </Button>
              )}
            </form.Subscribe>
          </form.AppForm>
        </form>
      </div>
    </div>
  );
}
